import * as React from 'react';
import { toArray } from '@rc-component/util';
import pickAttrs from '@rc-component/util/lib/pickAttrs';
import { clsx } from 'clsx';
import { useMergeSemantic } from '../_util/hooks';
import { cloneElement } from '../_util/reactNode';
import { devUseWarning } from '../_util/warning';
import { useComponentConfig } from '../config-provider/context';
import BreadcrumbContext from './BreadcrumbContext';
import BreadcrumbItem, { InternalBreadcrumbItem } from './BreadcrumbItem';
import BreadcrumbSeparator from './BreadcrumbSeparator';
import useStyle from './style';
import useItemRender from './useItemRender';
import useItems from './useItems';
const getPath = (params, path) => {
    if (path === undefined) {
        return path;
    }
    let mergedPath = (path || '').replace(/^\//, '');
    Object.keys(params).forEach((key) => {
        mergedPath = mergedPath.replace(`:${key}`, params[key]);
    });
    return mergedPath;
};
const Breadcrumb = (props) => {
    const { prefixCls: customizePrefixCls, separator, style, className, rootClassName, routes: legacyRoutes, items, children, itemRender, params = {}, classNames, styles, ...restProps } = props;
    const { getPrefixCls, direction, className: contextClassName, style: contextStyle, classNames: contextClassNames, styles: contextStyles, separator: contextSeparator, } = useComponentConfig('breadcrumb');
    const mergedSeparator = separator ?? contextSeparator ?? '/';
    let crumbs;
    const prefixCls = getPrefixCls('breadcrumb', customizePrefixCls);
    const [hashId, cssVarCls] = useStyle(prefixCls);
    const mergedItems = useItems(items, legacyRoutes);
    // =========== Merged Props for Semantic ==========
    const mergedProps = React.useMemo(() => {
        return {
            ...props,
            separator: mergedSeparator,
        };
    }, [props, mergedSeparator]);
    // ========================= Style ==========================
    const [mergedClassNames, mergedStyles] = useMergeSemantic([contextClassNames, classNames], [contextStyles, styles], {
        props: mergedProps,
    });
    if (process.env.NODE_ENV !== 'production') {
        const warning = devUseWarning('Breadcrumb');
        warning.deprecated(!legacyRoutes, 'routes', 'items');
        // Deprecated warning for breadcrumb children
        if (!mergedItems || mergedItems.length === 0) {
            const childList = toArray(children);
            warning.deprecated(childList.length === 0, 'Breadcrumb.Item and Breadcrumb.Separator', 'items');
            childList.forEach((element) => {
                if (element) {
                    warning(element.type &&
                        (element.type.__ANT_BREADCRUMB_ITEM === true ||
                            element.type.__ANT_BREADCRUMB_SEPARATOR === true), 'usage', "Only accepts Breadcrumb.Item and Breadcrumb.Separator as it's children");
                }
            });
        }
    }
    const mergedItemRender = useItemRender(prefixCls, itemRender);
    if (mergedItems && mergedItems.length > 0) {
        // generated by route
        const paths = [];
        const itemRenderRoutes = items || legacyRoutes;
        crumbs = mergedItems.map((item, index) => {
            const { path, key, type, menu, onClick, className: itemClassName, style, separator: itemSeparator, dropdownProps, } = item;
            const mergedPath = getPath(params, path);
            if (mergedPath !== undefined) {
                paths.push(mergedPath);
            }
            const mergedKey = key ?? index;
            if (type === 'separator') {
                return React.createElement(BreadcrumbSeparator, { key: mergedKey }, itemSeparator);
            }
            const itemProps = {};
            const isLastItem = index === mergedItems.length - 1;
            if (menu) {
                itemProps.menu = menu;
            }
            let { href } = item;
            if (paths.length && mergedPath !== undefined) {
                href = `#/${paths.join('/')}`;
            }
            return (React.createElement(InternalBreadcrumbItem, { key: mergedKey, ...itemProps, ...pickAttrs(item, { data: true, aria: true }), className: itemClassName, style: style, dropdownProps: dropdownProps, href: href, separator: isLastItem ? '' : mergedSeparator, onClick: onClick, prefixCls: prefixCls }, mergedItemRender(item, params, itemRenderRoutes, paths, href)));
        });
    }
    else if (children) {
        const childrenLength = toArray(children).length;
        crumbs = toArray(children).map((element, index) => {
            if (!element) {
                return element;
            }
            const isLastItem = index === childrenLength - 1;
            return cloneElement(element, {
                separator: isLastItem ? '' : mergedSeparator,
                // eslint-disable-next-line react/no-array-index-key
                key: index,
            });
        });
    }
    const breadcrumbClassName = clsx(prefixCls, contextClassName, { [`${prefixCls}-rtl`]: direction === 'rtl' }, className, rootClassName, mergedClassNames.root, hashId, cssVarCls);
    const mergedStyle = {
        ...mergedStyles.root,
        ...contextStyle,
        ...style,
    };
    const memoizedValue = React.useMemo(() => ({ classNames: mergedClassNames, styles: mergedStyles }), [mergedClassNames, mergedStyles]);
    return (React.createElement(BreadcrumbContext.Provider, { value: memoizedValue },
        React.createElement("nav", { className: breadcrumbClassName, style: mergedStyle, ...restProps },
            React.createElement("ol", null, crumbs))));
};
Breadcrumb.Item = BreadcrumbItem;
Breadcrumb.Separator = BreadcrumbSeparator;
if (process.env.NODE_ENV !== 'production') {
    Breadcrumb.displayName = 'Breadcrumb';
}
export default Breadcrumb;
